import { useEffect, useMemo, useState, type ReactNode } from 'react';

import { formatPersianNumber } from './formatters';
import { InlineFetchingIndicator } from './InlineFetchingIndicator';

export interface PaginatedListProps<T> {
  items: T[];
  renderItem: (item: T, index: number) => ReactNode;
  itemsPerPage?: number;
  totalItems?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  loading?: boolean;
  error?: ReactNode;
  emptyMessage?: ReactNode;
  className?: string;
}

/** Shared compact list policy for card and dashboard sections. */
export function PaginatedList<T>({
  items,
  renderItem,
  itemsPerPage = 5,
  totalItems,
  currentPage,
  onPageChange,
  loading = false,
  error,
  emptyMessage = 'موردی برای نمایش وجود ندارد.',
  className = '',
}: PaginatedListProps<T>) {
  const [localPage, setLocalPage] = useState(1);
  const isControlled = currentPage !== undefined && onPageChange !== undefined;
  const page = isControlled ? currentPage : localPage;
  const total = totalItems ?? items.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));

  useEffect(() => {
    if (!isControlled && localPage > totalPages) setLocalPage(totalPages);
  }, [isControlled, localPage, totalPages]);

  const visibleItems = useMemo(() => {
    if (isControlled) return items;
    const start = (page - 1) * itemsPerPage;
    return items.slice(start, start + itemsPerPage);
  }, [isControlled, items, itemsPerPage, page]);

  const changePage = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    if (isControlled) onPageChange(nextPage);
    else setLocalPage(nextPage);
  };

  if (loading) return <InlineFetchingIndicator />;
  if (error) return <div className="text-sm text-[var(--vania-accent-rose,#ff6b81)]">{error}</div>;
  if (visibleItems.length === 0) {
    return <p className="text-sm text-[var(--vania-text-tertiary,#6c7280)]">{emptyMessage}</p>;
  }

  return (
    <div className={className}>
      <ul className="flex list-none flex-col gap-2 p-0 m-0">
        {visibleItems.map(renderItem)}
      </ul>
      {totalPages > 1 ? (
        <div className="mt-3 flex items-center justify-between gap-3 text-sm">
          <button type="button" disabled={page <= 1 || loading} onClick={() => changePage(page - 1)}>
            قبلی
          </button>
          <span>
            صفحه {formatPersianNumber(page)} از {formatPersianNumber(totalPages)}
          </span>
          <button type="button" disabled={page >= totalPages || loading} onClick={() => changePage(page + 1)}>
            بعدی
          </button>
        </div>
      ) : null}
    </div>
  );
}
